import { Avatar, Box, Button, Chip, Grid, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import SimpleModal from 'src/Component/SimpleModal'
import { useGetUserDetailsByIdQuery, useUpdateUserActiveStatusMutation } from 'src/api/usersApi'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import { errorToast, successToast } from 'src/utils/toastService'

type ViewUserDetailsProps = {
  id: number
  open: boolean
  onClose: () => void
}
const ViewUserDetails = ({ id, open, onClose }: ViewUserDetailsProps) => {
  const [openSuspendConfirmation, setOpenSuspendConfirmation] = useState(false)
  const [openReActiveConfirmation, setOpenReActiveConfirmation] = useState(false)

  const dispatch = useDispatch<AppDispatch>()
  const { data, isFetching, refetch } = useGetUserDetailsByIdQuery(id)
  const [updateUserStatus, { isLoading }] = useUpdateUserActiveStatusMutation()

  const handleUpdateUserStatus = () => {
    const newActiveStatus = !data?.is_active
    updateUserStatus({
      id,
      isActive: newActiveStatus,
    })
      .unwrap()
      .then((res) => {
        if (newActiveStatus) {
          successToast('User re-activated successfully.')
        } else {
          successToast('User suspended successfully.')
        }
        refetch()
      })
      .catch((err) => {
        errorToast('Unable to change user status.')
      })
      .finally(() => {
        setOpenReActiveConfirmation(false)
        setOpenSuspendConfirmation(false)
      })
  }

  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  return (
    <Box>
      <SimpleModal open={!!(open && id)} onClose={onClose}>
        {!!data && (
          <Box>
            <Grid container justifyContent='space-between' alignItems='center' mb={5}>
              <Grid display='flex' alignItems='center' item>
                <Avatar src={data.profile_pic} sx={{ width: 60, height: 60 }} />
                <Typography variant='h2' pl={2}>
                  {`${data?.first_name} ${data?.last_name}`}
                </Typography>
              </Grid>
              <Grid item>
                {data?.is_active ? (
                  <Chip label='Active' color='success' />
                ) : (
                  <Chip label='Suspended' color='error' />
                )}
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Usertype</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'> {data?.user_type?.name} </Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>D.O.B</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'>{data.date_of_birth}</Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Height</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'>{data.height}</Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Weight</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'> {data.weight} </Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Address</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'></Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Latitude</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'></Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={3}>
                <Typography variant='h4'>Longitude</Typography>
              </Grid>
              <Grid xs={9}>
                <Typography variant='h5'></Typography>
              </Grid>
            </Grid>

            <Grid container mt={3}>
              {data.is_active ? (
                <Button
                  variant='contained'
                  color='primary'
                  type='button'
                  onClick={() => setOpenSuspendConfirmation(true)}
                >
                  SUSPEND
                </Button>
              ) : (
                <Button
                  variant='contained'
                  color='primary'
                  type='button'
                  onClick={() => setOpenReActiveConfirmation(true)}
                >
                  REACTIVATE
                </Button>
              )}
            </Grid>

            <ConfirmationBox
              title='Are you sure you want to Suspend user'
              open={openSuspendConfirmation}
              onReject={() => setOpenSuspendConfirmation(false)}
              onAccept={handleUpdateUserStatus}
            />

            <ConfirmationBox
              title='Are you sure you want to Reactivate user'
              open={openReActiveConfirmation}
              onReject={() => setOpenReActiveConfirmation(false)}
              onAccept={handleUpdateUserStatus}
            />
          </Box>
        )}
      </SimpleModal>
    </Box>
  )
}

export default ViewUserDetails
